import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sun, ArrowRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { InstallTip } from "@/components/InstallTip";
import { BoundaryCard } from "@/v2/DayCards";
import { PlansCard } from "@/v2/Plans";
import { EveningWrap } from "@/v2/EveningWrap";
import { MilestoneCard, MissionCard, ExperimentCard } from "@/v2/Growth";
import { RevealCard } from "@/v2/Pull";
import { Header, Momentum } from "@/v2/today3/Header";
import { BiggerPicture } from "@/v2/today3/GoalLadder";
import { MustDos } from "@/v2/today3/MustDos";
import { MonthSetup, MonthPrompt } from "@/v2/today3/MonthSetup";
import { StartMyDay } from "@/v2/today3/StartMyDay";
import { EveningCarry } from "@/v2/today3/EveningCarry";
import { MonthReview } from "@/v2/today3/MonthReview";
import { WeekSummary } from "@/v2/today3/WeekSummary";
import { ProgressActions } from "@/v2/today3/ProgressActions";
import { Suggestion } from "@/v2/today3/Suggestion";

function Launch({ day, onOpen }) {
  return (
    <button onClick={onOpen} data-testid="start-my-day-button" className="w-full text-left card p-5 sm:p-6 bg-[#2D2926] border-[#2D2926] text-stone-100 rise group">
      <span className="eyebrow !text-stone-300 flex items-center gap-1.5"><Sun className="h-3.5 w-3.5" />{day ? "Almost there" : "Check-in · 45 seconds"}</span>
      <span className="font-display text-3xl mt-2 block">{day ? "Finish planning your day" : "Start my day"}</span>
      <span className="text-sm text-stone-300 mt-2 block">{day ? "Pick your must-dos and lock them in." : "Check in, see your must-dos, lock them in."}</span>
      <span className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-stone-900 text-sm font-semibold transition-transform group-hover:translate-x-1">{day ? "Continue" : "Let's go"}<ArrowRight className="h-4 w-4" /></span>
    </button>
  );
}

export default function TodayV3({ t, setT, load, s, first, weightTargets, onClassic }) {
  const [b, setB] = useState(null);
  const [sheet, setSheet] = useState(false);
  const [month, setMonth] = useState(false);
  const [rev, setRev] = useState(null);
  const [wk, setWk] = useState(null);
  const [failed, setFailed] = useState(false);
  const loadBoard = useCallback(() => Promise.all([
    api.get("/v2/today/board").then(({ data }) => { setB(data); setFailed(false); toast.dismiss("today-board-error"); })
      .catch(() => { setFailed(true); toast.error("Couldn't load today's plan. Check your connection.", { id: "today-board-error" }); }),
    api.get("/v2/goals/review").then(({ data }) => setRev(data)).catch(() => {}),
    api.get("/v2/today/week").then(({ data }) => setWk(data)).catch(() => {}),
  ]), []);
  const all = async () => { await loadBoard(); await load(); };
  useEffect(() => { loadBoard(); }, [loadBoard]);
  if (!b) return failed ? (
    <div className="card p-5 sm:p-6 max-w-2xl" role="alert" data-testid="today-load-error">
      <p className="font-semibold text-stone-900">Today didn't load</p>
      <p className="text-sm text-stone-600 mt-1">Nothing was lost. Check your connection and try again.</p>
      <button onClick={loadBoard} data-testid="today-retry" className="btn-ink mt-4 h-11 px-5 rounded-full text-sm font-semibold">Try again</button>
    </div>
  ) : null;
  const d = t.day;
  const inToday = b.must_dos.map((m) => m.plan_id).filter(Boolean);
  const c = t.clock;
  const late = c.hour >= 19 || c.hour < c.day_start || c.personal_day !== c.calendar_date || (b.done_by && c.time >= b.done_by);
  const allDone = b.locked && b.momentum.total > 0 && b.momentum.done === b.momentum.total;
  const evening = d && b.locked && (late || allDone || !!d.wrap);
  const promote = async (p) => { try { await api.post("/v2/today/must/add", { plan_id: p.id }); toast.success("Added to today's must-dos."); all(); } catch (e) { toast.error(errMsg(e)); } };
  if (t.boundary) return <div className="max-w-2xl"><BoundaryCard b={t.boundary} clock={c} onDone={all} /></div>;
  return (
    <div className="space-y-6" data-testid="today-v3">
      <Header clock={c} day={d} style={t.journey.coaching_style} accent={s.phase.color}
        actions={<ProgressActions hasGoals={b.goals.length > 0} weightActive={!!t.weight_active} hasMission={!!t.mission} onEditGoals={() => setMonth(true)} />} />
      <div className="grid gap-6 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2 space-y-4 min-w-0" data-testid="today-main">
          <Suggestion board={b} mission={t.mission} onChooseDirection={() => setMonth(true)} />
          {b.locked ? <>
            <Momentum m={b.momentum} />
            {allDone && <p className="card p-4 sm:p-5 bg-[#EEF3EC] border-[#DCE6D8] text-sm text-[#2F4A35] font-semibold" data-testid="today-all-done">Every must-do is done. That's today handled.</p>}
            <MilestoneCard milestone={t.milestone} onSeen={all} />
            <MustDos board={b} onChange={all} />
          </> : <Launch day={d} onOpen={() => setSheet(true)} />}
          {evening && <EveningCarry board={b} onChange={all} />}
          {evening && <EveningWrap day={d} tomorrow={t.tomorrow} onWrap={(x) => setT((o) => ({ ...o, day: x.day, tomorrow: x.tomorrow }))} />}
          {d && weightTargets}
          <PlansCard title="My to-dos" refresh={`${b.momentum.done}-${b.must_dos.length}`} onToday={all} onPromote={b.locked ? promote : null} inToday={inToday} />
        </div>
        <aside className="space-y-4 min-w-0" data-testid="today-support">
          <WeekSummary w={wk} setW={setWk} />
          <MonthReview r={rev} setR={setRev} onEdit={() => setMonth(true)} onChange={all} />
          {b.needs_setup && !rev?.show && <MonthPrompt board={b} onOpen={() => setMonth(true)} onKept={all} />}
          <MissionCard mission={t.mission} rescue={d?.shape === "rescue"} onChange={all} />
          <RevealCard reveal={d?.reveal} />
          {d?.shape === "experiment" && <ExperimentCard goal={t.pulse?.goal} featured />}
          <BiggerPicture board={b} t={t} s={s} onEdit={() => setMonth(true)} onChange={all} />
        </aside>
      </div>
      <InstallTip trigger={b.momentum.done > 0} />
      {onClassic && <button onClick={onClassic} data-testid="today-switch-classic" className="h-11 text-xs text-[#8C857B] underline">Switch to the classic Today</button>}
      <StartMyDay open={sheet} onClose={() => setSheet(false)} t={t} setT={setT} first={first} reloadAll={all} onLocked={all} />
      <MonthSetup open={month} onClose={() => setMonth(false)} onSaved={all} />
    </div>
  );
}
